import { useEffect, useRef } from 'react';

// In-app "back" synced with browser history, so the Android back button/gesture
// and iOS Safari's edge-swipe close the current overlay/deep screen instead of
// leaving the app — plus a synthesized left-edge swipe for the installed iOS PWA
// (which has no native back gesture).
//
// Each open overlay/deep view registers via useBackHandler(active, onBack) and
// pushes one history entry. A back navigation (popstate) or the edge swipe pops
// the top entry and calls its onBack. Closing via the component's own UI removes
// the entry and rewinds the one history state we pushed, keeping the stack
// balanced.

const stack = [];      // LIFO of { onBack, popped }
let suppressPop = 0;   // # of popstates to ignore (rebalancing backs we triggered)
let installed = false;

function handlePop() {
  if (suppressPop > 0) { suppressPop -= 1; return; }
  const entry = stack.pop();
  if (entry) { entry.popped = true; entry.onBack(); }
}

function install() {
  if (installed) return;
  installed = true;
  window.addEventListener('popstate', handlePop);

  // iOS standalone PWA has no native edge-swipe — synthesize one. Skipped in
  // browsers / on Android, where the OS already drives a history-based back
  // (so we'd double-trigger if we also listened here).
  if (window.navigator.standalone === true) {
    let sx = null, sy = null, st = 0;
    const EDGE = 30, MIN_DX = 60, MAX_DY = 45, MAX_MS = 700;
    window.addEventListener('touchstart', (e) => {
      if (!stack.length || e.touches.length !== 1) { sx = null; return; }
      const t = e.touches[0];
      if (t.clientX <= EDGE) { sx = t.clientX; sy = t.clientY; st = Date.now(); }
      else sx = null;
    }, { passive: true });
    window.addEventListener('touchend', (e) => {
      if (sx == null) return;
      const t = e.changedTouches[0];
      if (t.clientX - sx > MIN_DX && Math.abs(t.clientY - sy) < MAX_DY && Date.now() - st < MAX_MS) {
        window.history.back();
      }
      sx = null;
    }, { passive: true });
  }
}

// Register a back action while `active` is true. onBack runs when the user
// navigates back (system back / swipe). Closing the thing by its own UI cleans
// up automatically.
export function useBackHandler(active, onBack) {
  const cb = useRef(onBack);
  cb.current = onBack;
  useEffect(() => {
    if (!active) return undefined;
    install();
    const entry = { onBack: () => cb.current(), popped: false };
    stack.push(entry);
    window.history.pushState({ swipeNav: stack.length }, '');
    return () => {
      const idx = stack.indexOf(entry);
      if (idx !== -1) stack.splice(idx, 1);
      if (!entry.popped) {
        // Closed by its own UI (not a back navigation): rewind our pushed entry.
        suppressPop += 1;
        window.history.back();
      }
    };
  }, [active]);
}
